import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Table, type TableColumn } from '@/_shared/components/Table';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';
import { CONTEXT_KIND_LABELS, getContextItemPath } from '@/_shared/lib/context-hub';
import { cn } from '@/_shared/lib/utils';
import { KIND_ICON } from '@/context-hub/components/contextItemDisplay';

import { type ContextResultItem, parseContextToolResult } from './context-tool-result';

/** Match the prior raw-result scroll budget (`max-h-48`) so list results can't flood the transcript. */
const RESULT_MAX_HEIGHT = '12rem';

function itemLabel(item: ContextResultItem): string {
  return CONTEXT_KIND_LABELS[item.kind];
}

function ItemIcon({ item }: { item: ContextResultItem }): React.ReactElement {
  const Icon = KIND_ICON[item.kind];
  return <Icon className="size-3" aria-hidden="true" data-testid={`context-icon-${itemLabel(item).toLowerCase()}`} />;
}

export function ContextToolResult({ toolName, content }: { toolName: string; content: string }): React.ReactElement {
  const workspace = useWorkspaceOrNull();
  const result = useMemo(() => parseContextToolResult(toolName, content), [toolName, content]);

  const columns = useMemo<TableColumn<ContextResultItem>[]>(
    () => [
      {
        id: 'item',
        header: '',
        headerAriaLabel: 'Context item',
        cell: (item) => (
          <div className="flex min-w-0 items-center gap-2">
            <Badge variant={item.isSkill ? 'highlight' : 'outline'} bordered={item.isSkill} className="shrink-0">
              <ItemIcon item={item} />
              {itemLabel(item)}
            </Badge>
            <span className="min-w-0 truncate font-medium text-foreground">{item.title}</span>
          </div>
        ),
      },
      {
        id: 'action',
        header: '',
        headerAriaLabel: 'Open context item',
        align: 'right',
        width: '5rem',
        cell: (item) => {
          const ariaLabel = `View ${itemLabel(item)}: ${item.title}`;
          if (!workspace) {
            return <Button variant="secondary" size="sm" disabled label="View" aria-label={ariaLabel} />;
          }
          return (
            <Button variant="secondary" size="sm" asChild>
              <Link to={workspace.wp(getContextItemPath(item))} aria-label={ariaLabel}>
                View
              </Link>
            </Button>
          );
        },
      },
    ],
    [workspace],
  );

  if (!result || result.state === 'unavailable') {
    return <p className="text-muted-foreground">{result?.message ?? 'Context details unavailable.'}</p>;
  }

  const isConsulted = result.label === 'Consulted context';

  if (result.state === 'empty') {
    return (
      <div data-testid="context-tool-result">
        {isConsulted ? <p className="font-medium text-foreground">{result.label}</p> : null}
        <p className={isConsulted ? 'mt-1 text-muted-foreground' : 'text-muted-foreground'}>No context found.</p>
      </div>
    );
  }

  return (
    <div data-testid="context-tool-result">
      {isConsulted ? <p className="font-medium text-foreground">{result.label}</p> : null}
      <Table
        data={result.items}
        columns={columns}
        getRowId={(item) => `${item.kind}:${item.id}`}
        hideHeader
        // Long titles must truncate so View stays in the 5rem column (see Table `fixedLayout`).
        fixedLayout
        maxHeight={RESULT_MAX_HEIGHT}
        className={cn(isConsulted && 'mt-2')}
      />
    </div>
  );
}
